import type { ProcessDialogCommand, ProcessResultDto } from '@/api-gen/api.schemas';
import type { ProcessResultDto as LegacyProcessResultDto } from '@/api-gen/api.schemas';
import { DialogProcessorActions } from 'app/actions/dialogprocessor/dialogProcessorActions.ts';
import { VoiceRecognitionActions } from 'app/actions/voiceRecognition/voiceRecognitionActions.ts';
import { processProcessDialog, speechRecognizeSpeech } from '@/api-gen/api.ts';
import * as DialogProcessorSelector from 'app/selectors/dialogProcessorSelector.ts';
import * as RecognitionSelector from 'app/selectors/voiceRecognitionSelector.ts';
import * as ScenarioChartSelectors from 'app/selectors/scenarioChartSelectors.ts';
import * as ScenarioSelectors from 'app/selectors/scenarioSelectors.ts';
import type { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import type { Action } from 'redux-actions';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import * as ScenarioActions from 'app/actions/scenarios/scenarioAction.ts';
import logger from 'app/utils/logger.ts';
import VoiceRecognitionStatus from 'app/models/voiceRecognition/VoiceRecognitionStatus.ts';

const mimeToExt: Record<string, string> = {
  'audio/webm': 'webm',
  'audio/webm;codecs=opus': 'webm',
  'audio/ogg': 'ogg',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  'audio/x-wav': 'wav',
  'audio/mp4': 'm4a',
  'audio/aac': 'aac',
  'audio/3gpp': '3gp',
  'audio/3gpp2': '3g2'
};

const guessFilenameFromBlob = (blob: Blob) => {
  const type = (blob.type || '').toLowerCase();
  const baseType = type.split(';')[0];
  const ext = mimeToExt[baseType] ?? (baseType.includes('/') ? baseType.split('/')[1] : 'bin');
  return `audio.${ext}`;
};

function* getAudioOfLastIntent(action: Action<{ audio: null | Blob; speechBase64?: string }>) {
  const speechBase64 = action.payload.speechBase64 ?? '';

  try {
    const language: string = yield select(ScenarioSelectors.currentChartScenarioLang);
    const formData = new FormData();
    if (action.payload.audio) {
      formData.append('file', action.payload.audio, guessFilenameFromBlob(action.payload.audio));
    }
    const result: AxiosResponse<string> = yield call(speechRecognizeSpeech, formData as any, { lng: language });

    const recognizedText = result.data?.trim();

    if (recognizedText === '##!!!ERROR_STRING!!!##') {
      // fallback to browser STT
      yield put(VoiceRecognitionActions.setUseFallbackStt(true));
      const fallbackString = yield select(RecognitionSelector.fallbackSttTextSelector);

      yield put(VoiceRecognitionActions.setLatestInhouseText(fallbackString));
      yield put(
        DialogProcessorActions.getResponseData({
          speechBase64,
          text: fallbackString
        })
      );
    } else {
      yield put(VoiceRecognitionActions.setLatestInhouseText(recognizedText));
      yield put(
        DialogProcessorActions.getResponseData({
          speechBase64,
          text: recognizedText
        })
      );
    }
  } catch (e) {
    logger.error(e);

    yield put(VoiceRecognitionActions.setLatestInhouseText(''));
    yield put(DialogProcessorActions.getResponseData({ text: '', speechBase64 }));
  }
}

export default function* watchSpeechToTextSaga() {
  yield all([yield takeLatest(VoiceRecognitionActions.Type.PARSE_LATEST_AUDIO, getAudioOfLastIntent)]);
}

const RECOGNIZE_SPEECH_ERROR = '##!!!ERROR_STRING!!!##';

// Replace multiple redux functions into one or several "react" functions
export const useSpeechToTextHelpers = () => {
  const dispatch = useDispatch();
  const currentChart = useSelector(ScenarioChartSelectors.scenarioChart);
  const scenarioLinks = useSelector(ScenarioSelectors.linkedScenarios);
  const currentChartScenario = useSelector(ScenarioSelectors.currentChartScenario);
  const lastResponse = useSelector(DialogProcessorSelector.dialogProcessorSelector);
  const language = useSelector(ScenarioSelectors.currentChartScenarioLang);
  const fallbackString = useSelector(RecognitionSelector.fallbackSttTextSelector);
  const useStreaming = useSelector(ScenarioSelectors.isStreamingEnabledForScenario);

  const getDPResponseNoRedux = useCallback(
    async (props: { text: string; speechBase64?: string }) => {
      const { text: currentMessage, speechBase64 } = props;
      // getResponseData -> getDPResponse in dialogProcessorSaga
      const lastBlock = !!lastResponse.blocks.currentBlock ? lastResponse.blocks.currentBlock : null;
      const intentProcessingProvider = (currentChartScenario as any).intentProcessingProvider;

      try {
        const payload: ProcessDialogCommand = {
          sessionId: lastResponse.sessionId,
          text: currentMessage,
          speech: speechBase64
        };

        const response: AxiosResponse<ProcessResultDto> = await processProcessDialog(payload);

        // also used in VoiceRecognitionReducer
        if (!useStreaming) {
          // Cast to legacy type to keep reducer/action typings stable during migration
          dispatch(DialogProcessorActions.setResponseDataStore(response.data as unknown as LegacyProcessResultDto));
        }

        if (response.data.lastBlock && response.data.lastBlock.sceneId != currentChart.properties.id) {
          dispatch(ScenarioActions.toNextLinkedScenario(true));
          dispatch(
            ScenarioActions.switchCurrentScenarioTo(
              scenarioLinks.findIndex((x) => x.sceneId == response.data.lastBlock.sceneId)
            )
          );
        }
      } catch (error) {
        logger.log('Error getDPResponse: ', error);
      } finally {
        dispatch(VoiceRecognitionActions.setUseFallbackStt(false));
      }
    },
    [
      currentChart.properties.id,
      (currentChartScenario as any).intentProcessingProvider,
      dispatch,
      lastResponse.blocks.currentBlock,
      lastResponse.sessionId,
      scenarioLinks
    ]
  );

  const getAudioOfLastIntentNoRedux = useCallback(
    async ({ audio, speechBase64 = '' }: { audio: null | Blob; speechBase64?: string }) => {
      let text: string;
      try {
        const formData = new FormData();
        if (audio) {
          formData.append('file', audio, guessFilenameFromBlob(audio));
        }
        const result = (await speechRecognizeSpeech(formData as any, {
          lng: language
        })) as unknown as AxiosResponse<string>;

        const recognizedText = result.data?.trim();
        text = recognizedText;

        if (recognizedText === RECOGNIZE_SPEECH_ERROR) {
          // fallback to browser STT
          text = fallbackString;

          dispatch(VoiceRecognitionActions.setUseFallbackStt(true));
        }
      } catch (e) {
        logger.error(e);
      }

      // yield put(DialogProcessorActions.getResponseData({ text, speechBase64 }));
      dispatch(VoiceRecognitionActions.setLatestInhouseText(text));
      getDPResponseNoRedux({ text, speechBase64 });
    },
    [dispatch, fallbackString, getDPResponseNoRedux, language]
  );

  return {
    getAudioOfLastIntentNoRedux,
    getDPResponseNoRedux
  };
};
